/**
 * LegacyService
 *
 * @author Copyright (c) 2021 Salesforce.com.
 * @ownership Appexchange Experience
 */
import { INVENTORY_API } from '~/modules/config';
import { http, HttpRequestConfig } from '~/modules/utilities/http';
import { convertToMoment } from '../utilities/time';

export interface Install {
    id: string;
    name: string;
    url: string;
    logoUrl: string;
    logoText?: string;
    lastInstallDate: string;
}

export interface LegacyStatus {
    isLoading: boolean;
    isError: boolean;
    isEmpty: boolean;
    errorMessage: string;
}

export interface LegacyListing {
    appExchangeId: string;
    tzId: string;
    oafId: string;
    averageRating: number;
    reviewsAmount: number;
    name: string;
    publisherName: string;
    type: string;
    logoUrl: string;
    description: string;
    url?: string;
}

export interface LegacyInstalls {
    installs: Install[];
    currentInstalls: string;
}

export interface LegacyInstallsState extends LegacyStatus, LegacyInstalls {}

export class LegacyService {
    endpoint = INVENTORY_API;

    async getInstalls(language: string, imageLabel: string | ((args: AnyObject) => string)): Promise<LegacyInstalls> {
        const httpConfig: HttpRequestConfig = {};
        const response = await http.get(`${this.endpoint}?filter=recentInstalls&lang=${language}`, httpConfig);

        const result = JSON.parse(response.data);
        // @ts-expect-error * Automated Type Error Fix
        const installs: Install[] = result.data.map((listing) => {
            // This text for images is not in the legacy api, so we add a default text explaining that is the listing logo
            const defaultImageText = typeof imageLabel === 'string' ? imageLabel : imageLabel({ NAME: listing.name });
            return {
                id: listing.id,
                externalId: listing.externalId,
                name: listing.name,
                logoUrl: listing.logoUrl,
                logoText: defaultImageText,
                url: listing.url,
                lastInstallDate: convertToMoment(listing.lastInstallDate, language),
            };
        });

        const currentInstalls = Number.isNaN(result.currentInstalls)
            ? ''
            : new Intl.NumberFormat().format(parseInt(result.currentInstalls));

        return {
            installs,
            currentInstalls,
        };
    }
}
